/* =====================================================================
   HOTHANNI V6 (Codext 07.10.2026)
   Kategorie: Schnellfilter per AJAX, Sortieren per nativem Auswahlfeld,
   "Mehr anzeigen", Kategoriebeschreibung aufklappen, Suche in langen Filterlisten.
   Nutzt das Event "facet:update" vom Theme, dadurch identisches Verhalten
   wie die eingebauten Filter (URL, Ladezustand, Scrollen).
   ===================================================================== */
(() => {
  if (window.__hhV6) return;
  window.__hhV6 = true;

  const MAIN_SEL = '.shopify-section--main-collection';
  const TOOLS_SEL = '.shopify-section--hh-collection-tools';
  const mainSection = () => document.querySelector(MAIN_SEL);
  const toolsSection = () => document.querySelector(TOOLS_SEL);
  const idOf = (el) => el.id.replace('shopify-section-', '');

  const update = (url) => {
    const main = mainSection();
    if (!main) return false;
    url.searchParams.delete('page');
    url.searchParams.set('section_id', idOf(main));
    main.dispatchEvent(new CustomEvent('facet:update', { bubbles: true, detail: { url } }));
    return true;
  };

  /* ---------- Schnellfilter Chips ---------- */
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[data-hh-facet]');
    if (!a || e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
    const url = new URL(a.href, location.origin);
    // Vorschau Parameter mitnehmen, sonst springt der Theme Editor ins Live Theme
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    if (update(url)) {
      e.preventDefault();
      a.classList.toggle('is-active');
      a.classList.add('is-pending');
    }
  });

  /* ---------- Sortieren (natives select, auf dem Handy der System Picker) ---------- */
  document.addEventListener('change', (e) => {
    const s = e.target.closest('select[data-hh-sort]');
    if (!s) return;
    const url = new URL(location.href);
    url.searchParams.set('sort_by', s.value);
    const label = s.closest('label')?.querySelector('.hh-tools__sort-value');
    if (label) label.textContent = s.options[s.selectedIndex].text;
    if (!update(url)) location.href = url.toString();
  });

  /* ---------- Schnellfilter nach jedem Filterwechsel neu laden ---------- */
  let toolsAbort = null;
  const refreshTools = () => {
    const tools = toolsSection();
    if (!tools) return Promise.resolve();
    toolsAbort?.abort();
    toolsAbort = new AbortController();
    const url = new URL(location.href);
    url.searchParams.set('section_id', idOf(tools));
    return fetch(url.toString(), { signal: toolsAbort.signal, credentials: 'same-origin' })
      .then((r) => r.text())
      .then((html) => {
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
        if (fresh) tools.replaceChildren(...document.importNode(fresh, true).childNodes);
      })
      .catch(() => {});
  };

  /* ---------- Mehr anzeigen ---------- */
  const setupMore = () => {
    const main = mainSection();
    if (!main) return;
    main.querySelector('.hh-more')?.remove();
    const nav = main.querySelector('.collection__pagination');
    const next = nav?.querySelector('a[rel="next"]');
    const list = main.querySelector('product-list.product-list');
    if (!nav || !next || !list) return;

    const shown = list.querySelectorAll('product-card').length;
    const count = parseInt(document.querySelector('.hh-tools')?.dataset.count || '0', 10);
    const pct = count ? Math.min(100, Math.round((shown / count) * 100)) : 0;

    const wrap = document.createElement('div');
    wrap.className = 'hh-more';
    wrap.innerHTML =
      (count ? `<p class="hh-more__info">${shown} von ${count} Produkten</p><div class="hh-more__bar" aria-hidden="true"><span style="width:${pct}%"></span></div>` : '') +
      '<button type="button" class="hh-more__btn">Mehr anzeigen</button>';
    nav.before(wrap);

    const btn = wrap.querySelector('button');
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      btn.classList.add('is-loading');
      btn.textContent = 'Lädt …';
      const url = new URL(next.href, location.origin);
      url.searchParams.set('section_id', idOf(main));
      try {
        const html = await (await fetch(url.toString(), { credentials: 'same-origin' })).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const items = doc.querySelectorAll('product-list.product-list > *');
        if (!items.length) throw new Error('leer');
        const firstNew = document.importNode(items[0], true);
        list.appendChild(firstNew);
        for (let i = 1; i < items.length; i++) list.appendChild(document.importNode(items[i], true));
        const newNav = doc.querySelector('.collection__pagination');
        newNav ? nav.replaceWith(document.importNode(newNav, true)) : nav.remove();
        setupMore();
        firstNew.querySelector('a')?.focus({ preventScroll: true });
      } catch (err) {
        location.href = next.href;
      }
    });
  };

  /* ---------- Kategoriebeschreibung: Mehr lesen (nur wenn gekürzt) ---------- */
  const setupDesc = () => {
    document.querySelectorAll('.hh-cat__desc').forEach((d) => {
      if (d.dataset.hhInit) return;
      d.dataset.hhInit = '1';
      if (d.scrollHeight - d.clientHeight < 6) return;
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'hh-cat__more';
      b.textContent = 'Mehr lesen';
      b.setAttribute('aria-expanded', 'false');
      b.addEventListener('click', () => {
        const open = d.classList.toggle('is-open');
        b.textContent = open ? 'Weniger' : 'Mehr lesen';
        b.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      d.after(b);
    });
  };

  /* ---------- Suche in langen Filterlisten (z.B. ~200 Marken) ---------- */
  const norm = (v) => v.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const setupFacetSearch = () => {
    document.querySelectorAll('.facets-vertical .checkbox-list[data-hh-label]').forEach((list) => {
      if (list.dataset.hhSearch) return;
      const items = list.querySelectorAll('.checkbox-container');
      if (items.length < 13) return;
      list.dataset.hhSearch = '1';
      const label = list.dataset.hhLabel || 'Filter';
      const wrap = document.createElement('div');
      wrap.className = 'hh-fsearch';
      const input = document.createElement('input');
      input.type = 'search';
      input.className = 'hh-fsearch__input';
      input.placeholder = label + ' suchen';
      input.setAttribute('aria-label', label + ' suchen');
      input.autocomplete = 'off';
      input.setAttribute('enterkeyhint', 'search');
      const empty = document.createElement('p');
      empty.className = 'hh-fsearch__empty';
      empty.hidden = true;
      empty.textContent = 'Nichts gefunden';
      wrap.append(input, empty);
      list.before(wrap);
      // Eingaben nicht an das Filterformular weitergeben, sonst lädt es bei jedem Tippen neu
      input.addEventListener('input', (e) => {
        e.stopPropagation();
        const q = norm(input.value.trim());
        let hits = 0;
        items.forEach((it) => {
          const show = !q || norm(it.textContent).includes(q) || !!it.querySelector('input:checked');
          it.hidden = !show;
          if (show) hits++;
        });
        empty.hidden = hits > 0;
      });
      input.addEventListener('change', (e) => e.stopPropagation());
      input.addEventListener('keydown', (e) => { if (e.key === 'Enter') e.preventDefault(); });
    });
  };

  const init = () => {
    setupDesc();
    setupMore();
    setupFacetSearch();
    const main = mainSection();
    if (!main) return;
    let t = null;
    // Das Theme ersetzt nach jedem Filterwechsel den Inhalt der Section komplett
    new MutationObserver(() => {
      clearTimeout(t);
      setupFacetSearch();
      t = setTimeout(() => refreshTools().then(setupMore), 30);
    }).observe(main, { childList: true });
  };

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init) : init();
})();
